// 09-10 (Pau aprobó la maqueta «mantener el dedo», iPad): el menú de un agente en la escena, al mantener el dedo (o
// con el clic derecho). Lo mismo que se hace desde su chat, sin tener que abrirlo: abrir, renombrar (abre el chat con el
// nombre listo para cambiar: `renombrarPedido` del store), moverlo a un grupo o de mesa, aparcarlo o despertarlo y
// terminar su trabajo (abre el chat con su diálogo de cierre ya abierto: `cierrePedido` del store).
// 04-11 (mesas de equipo, grupos.md §4): «Mover a ▸» = los grupos de su sala, «Sin grupo», «Grupo nuevo… Ctrl G» y
// «Mover de mesa» (abre «Ordenar»). Con varias mesas elegidas (Ctrl+clic) y esta entre ellas: «Agrupar los N…».
// Mismo aspecto que el menú de la cabecera del chat (chat/VentanaChat.tsx).
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, DoorOpen, FolderInput, Group, Handshake, LayoutGrid, MessageCircle, Moon, Pencil, Plus, Sun, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useOficinaStore } from '../store';
import { t } from '../../../i18n';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { quien } from '../marco/estado';

const OPCION = 'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-oficina-control data-[state=open]:bg-oficina-control';

function Opcion({ icono: Icono, atajo, extra, alElegir, children }: { icono: LucideIcon | null; atajo?: string; extra?: ReactNode; alElegir: () => void; children: ReactNode }) {
  return (
    // ≥ 44 px de alto: se pulsa con el dedo.
    <Menu.Item onSelect={alElegir} className={OPCION}>
      {Icono ? <Icono className="h-4 w-4 shrink-0 text-oficina-texto2" aria-hidden /> : <span className="h-4 w-4 shrink-0" aria-hidden />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {extra}
      {atajo ? <kbd className="shrink-0 font-sans text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{atajo}</kbd> : null}
    </Menu.Item>
  );
}

/** Un destino de «Mover a»: un grupo de la sala (con cuántos tiene), o una sugerencia (vaciado hace < 7 días, n = 0). */
export interface DestinoGrupo {
  grupoId: string;
  nombre: string;
  n: number;
  /** Espera a Santi ahora (su contrato atado tiene la pelota en el lado de Santi). */
  conSanti: boolean;
}

export interface MoverAGrupo {
  grupos: readonly DestinoGrupo[];
  /** El grupo en el que está (null = «Sin grupo»). */
  actual: string | null;
  alMover: (grupoId: string | null) => void;
  alGrupoNuevo: () => void;
  /** Hay varias mesas elegidas y esta es una: «Agrupar los N…». */
  agrupar?: { n: number; alAgrupar: () => void };
}

export function MenuAgente({
  c,
  alCerrar,
  alAbrir,
  alMover,
  aparcado = false,
  grupos,
}: {
  c: ContratadoDTO;
  alCerrar: () => void;
  alAbrir: () => void;
  /** «Mover de mesa»: abre «Ordenar» de su sala. */
  alMover: () => void;
  /** Aparcado ahora (lo resuelve la escena con el mismo cálculo que la lista). */
  aparcado?: boolean;
  /** 04-11 — «Mover a ▸»: los grupos de su sala. Sin esto, solo «Mover de mesa» (como antes). */
  grupos?: MoverAGrupo;
}) {
  // 04-11 (Pau aprobó «Aparcar»; «Fijar arriba» se fue con el grupo «Fijados»): lo mismo que el ⋮ del chat. Lo que
  // trabaja o se va no se aparca.
  const quieto = !['trabajando', 'llegando', 'cerrando', 'saliendo'].includes(c.situacion);
  const aparcar = (si: boolean) => void useOficinaStore.getState().aparcar(c.agentId, si);
  return (
    <Menu.Root open onOpenChange={(abierto) => !abierto && alCerrar()}>
      {/* El ancla es la mesa entera: el menú sale encima (o debajo) de ella, nunca bajo el dedo — Radix elige la
          opción sobre la que se LEVANTA el dedo, y el dedo sigue sobre el agente al abrirse. */}
      <Menu.Trigger asChild>
        <span className="pointer-events-none absolute inset-0" aria-hidden tabIndex={-1} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={8}
          // El ancla no tiene texto: el menú se nombra a sí mismo (Radix lo nombraría por el ancla).
          aria-labelledby={undefined}
          aria-label={t('oficinaV2.escena.acciones', { nombre: quien(c) })}
          className="z-[70] min-w-56 rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja animate-in fade-in-0 zoom-in-95"
        >
          {grupos?.agrupar ? (
            <>
              <Opcion icono={Group} atajo={t('oficinaV2.grupos.atajoAgrupar')} alElegir={grupos.agrupar.alAgrupar}>
                {t('oficinaV2.grupos.agruparLos', { n: grupos.agrupar.n })}
              </Opcion>
              <Menu.Separator className="my-1 h-px bg-oficina-linea" />
            </>
          ) : null}
          <Opcion icono={MessageCircle} alElegir={alAbrir}>
            {t('oficinaV2.escena.abrirChat')}
          </Opcion>
          {grupos ? (
            <Opcion
              icono={Pencil}
              atajo="F2"
              alElegir={() => {
                useOficinaStore.getState().pedirRenombrar(c.agentId);
                alAbrir();
              }}
            >
              {t('oficinaV2.grupos.renombrarAgente')}
            </Opcion>
          ) : null}
          {grupos ? (
            <Menu.Sub>
              <Menu.SubTrigger className={OPCION} data-testid="mover-a">
                <FolderInput className="h-4 w-4 shrink-0 text-oficina-texto2" aria-hidden />
                <span className="flex-1">{t('oficinaV2.grupos.moverA')}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
              </Menu.SubTrigger>
              <Menu.Portal>
                <Menu.SubContent
                  sideOffset={4}
                  collisionPadding={8}
                  aria-label={t('oficinaV2.grupos.moverA')}
                  className="z-[71] max-h-96 min-w-60 overflow-y-auto rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja animate-in fade-in-0 zoom-in-95"
                >
                  {grupos.grupos.map((g) => (
                    <Opcion
                      key={g.grupoId}
                      icono={grupos.actual === g.grupoId ? Check : null}
                      alElegir={() => grupos.alMover(g.grupoId)}
                      extra={
                        <span className="flex shrink-0 items-center gap-1 text-o-sub tabular-nums text-oficina-texto3">
                          {g.conSanti ? <Handshake className="h-3.5 w-3.5 text-oficina-santi" aria-label={t('oficinaV2.grupos.esperandoSanti')} /> : null}
                          {g.n ? g.n : t('oficinaV2.grupos.sugerido')}
                        </span>
                      }
                    >
                      {g.nombre}
                    </Opcion>
                  ))}
                  <Opcion icono={grupos.actual === null ? Check : null} alElegir={() => grupos.alMover(null)}>
                    {t('oficinaV2.grupos.sinGrupo')}
                  </Opcion>
                  <Menu.Separator className="my-1 h-px bg-oficina-linea" />
                  <Opcion icono={Plus} atajo={t('oficinaV2.grupos.atajoAgrupar')} alElegir={grupos.alGrupoNuevo}>
                    {t('oficinaV2.grupos.grupoNuevo')}
                  </Opcion>
                  <Opcion icono={LayoutGrid} alElegir={alMover}>
                    {t('oficinaV2.mesas.moverDeMesa')}
                  </Opcion>
                </Menu.SubContent>
              </Menu.Portal>
            </Menu.Sub>
          ) : (
            <Opcion icono={LayoutGrid} alElegir={alMover}>
              {t('oficinaV2.mesas.moverDeMesa')}
            </Opcion>
          )}
          {aparcado ? (
            <Opcion icono={Sun} alElegir={() => aparcar(false)}>
              {t('oficinaV2.lista.aparcado.despertar')}
            </Opcion>
          ) : quieto ? (
            <Opcion icono={Moon} alElegir={() => aparcar(true)}>
              {t('oficinaV2.lista.aparcado.aparcar')}
            </Opcion>
          ) : null}
          {c.ciclo === 'activo' ? (
            <>
              {grupos ? <Menu.Separator className="my-1 h-px bg-oficina-linea" /> : null}
              <Opcion
                icono={DoorOpen}
                alElegir={() => {
                  useOficinaStore.getState().pedirCierre(c.agentId);
                  alAbrir();
                }}
              >
                {t('oficinaV2.cierre.boton')}
              </Opcion>
            </>
          ) : null}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
